import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';

// Функция для динамической загрузки GTM
function loadGTM(gtmId = 'GTM-PGWWQ4VJ') {
  if (document.getElementById('gtm-script')) return; // уже загружен
  const script = document.createElement('script');
  script.id = 'gtm-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  document.head.appendChild(script);

  // Добавляем noscript-iframe (если нужно)
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  document.body.appendChild(noscript);
}

// Функция для динамической загрузки GA (gtag.js)
function loadGA(gaId = 'G-5DJLQW5BZK') {
  if (document.getElementById('ga-script')) return; // если уже загружен

  const script = document.createElement('script');
  script.id = 'ga-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);
  script.onload = () => {
    window.dataLayer = window.dataLayer || [];
    function gtag(){ window.dataLayer.push(arguments); }  // здесь используем window.dataLayer
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', gaId);
  };
}


function CookieConsentModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);

  // Состояния для отдельных категорий
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // 1) При монтировании проверяем, есть ли согласие в localStorage
    const localConsent = localStorage.getItem('cookie_consent_status');
    if (!localConsent) {
      setIsOpen(true);
    }

    // 2) Слушаем кастомное событие "openConsentModal"
    const handleOpenConsentModal = (e) => {
      setIsOpen(true);
      if (e.detail?.showCustomize) {
        setShowCustomize(true);
      }
    };
    window.addEventListener('openConsentModal', handleOpenConsentModal);
    return () => {
      window.removeEventListener('openConsentModal', handleOpenConsentModal);
    };
  }, []);

  // Сгенерировать уникальный ID для consent
  const generateConsentId = () => uuidv4();

  // =========================
  // Обработчики выбора
  // =========================

  // Принять все куки
  const handleAcceptAll = () => {
    localStorage.setItem('cookie_consent_status', 'all');
    const consentId = generateConsentId();
    const now = new Date().toISOString();

    Cookies.set('cookie_consent', 'all', { expires: 365 });
    Cookies.set('cookie_consent_id', consentId, { expires: 365 });
    Cookies.set('cookie_consent_date', now, { expires: 365 });
    Cookies.set(
      'cookie_preferences',
      JSON.stringify({
        necessary: true,
        analytics: true,
        marketing: true,
      }),
      { expires: 365 }
    );

    // Загружаем GA и GTM после согласия
    loadGTM();
    loadGA();

    setIsOpen(false);
    setShowCustomize(false);
  };

  // Отклонить все
  const handleRejectAll = () => {
    localStorage.setItem('cookie_consent_status', 'necessary');
    const consentId = generateConsentId();
    const now = new Date().toISOString();

    Cookies.set('cookie_consent', 'necessary', { expires: 365 });
    Cookies.set('cookie_consent_id', consentId, { expires: 365 });
    Cookies.set('cookie_consent_date', now, { expires: 365 });
    Cookies.set(
      'cookie_preferences',
      JSON.stringify({
        necessary: true,
        analytics: false,
        marketing: false,
      }),
      { expires: 365 }
    );

    setIsOpen(false);
    setShowCustomize(false);
  };

  // Открыть настройки
  const handleCustomize = () => {
    setShowCustomize(true);
  };

  // Сохранить пользовательские настройки
  const handleSavePreferences = () => {
    localStorage.setItem('cookie_consent_status', 'custom');
    const consentId = generateConsentId();
    const now = new Date().toISOString();

    Cookies.set('cookie_consent', 'custom', { expires: 365 });
    Cookies.set('cookie_consent_id', consentId, { expires: 365 });
    Cookies.set('cookie_consent_date', now, { expires: 365 });
    Cookies.set(
      'cookie_preferences',
      JSON.stringify({
        necessary: true,
        analytics: analytics,
        marketing: marketing,
      }),
      { expires: 365 }
    );

    // Если пользователь разрешил аналитику, загружаем GA и GTM
    if (analytics) {
      loadGTM();
      loadGA();
    }

    setIsOpen(false);
    setShowCustomize(false);
  };

  // Переоткрыть настройки (мини-кнопка)
  const handleReopenModal = () => {
    setIsOpen(true);
    setShowCustomize(false);
  };

  return (
    <>
      {/* Модальное окно №1: баннер (Accept / Reject / Customize) */}
      {isOpen && !showCustomize && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="relative w-full max-w-md rounded-md shadow dark:shadow-gray-800 bg-white dark:bg-slate-900 p-6 mx-4">
            <h2 className="text-xl font-semibold mb-2 dark:text-white">
              We use cookies
            </h2>
            <p className="text-slate-600 dark:text-slate-300 mb-4">
              This website uses cookies to improve your experience. You can manage your cookie preferences below.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                className="btn bg-violet-600 hover:bg-violet-700 border-violet-600 text-white rounded-md px-4 py-2"
                onClick={handleAcceptAll}
              >
                Accept All
              </button>
              <button
                className="btn bg-gray-300 hover:bg-gray-400 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-white rounded-md px-4 py-2"
                onClick={handleRejectAll}
              >
                Reject All
              </button>
              <button
                className="btn bg-violet-50 hover:bg-violet-100 dark:bg-slate-800 dark:hover:bg-slate-700 border border-violet-600 text-violet-600 dark:text-white rounded-md px-4 py-2"
                onClick={handleCustomize}
              >
                Customize
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно №2: Customize (чекбоксы) */}
      {isOpen && showCustomize && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="relative w-full max-w-md rounded-md shadow dark:shadow-gray-800 bg-white dark:bg-slate-900 p-6 mx-4">
            <h2 className="text-xl font-semibold mb-2 dark:text-white">
              Cookie Preferences
            </h2>
            <p className="text-slate-600 dark:text-slate-300 mb-4">
              Adjust your cookie preferences below.
            </p>
            <div className="mb-6 space-y-3">
              {/* Necessary (всегда включены) */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="mr-2 accent-violet-600"
                  onChange={() => {}}
                />
                <span className="text-slate-700 dark:text-slate-200">
                  Necessary (always required)
                </span>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="mr-2 accent-violet-600"
                />
                <span className="text-slate-700 dark:text-slate-200">
                  Analytics
                </span>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="mr-2 accent-violet-600"
                />
                <span className="text-slate-700 dark:text-slate-200">
                  Marketing
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                className="btn bg-violet-600 hover:bg-violet-700 border-violet-600 text-white rounded-md px-4 py-2"
                onClick={handleSavePreferences}
              >
                Save preferences
              </button>
              <button
                className="btn bg-gray-300 hover:bg-gray-400 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-white rounded-md px-4 py-2"
                onClick={handleRejectAll}
              >
                Reject All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Плавающая кнопка для повторного открытия модалки */}
      <button
        onClick={handleReopenModal}
        className="fixed bottom-4 end-4 bg-violet-600 hover:bg-violet-700 text-white rounded-full w-12 h-12 flex items-center justify-center z-50 p-0 border-0 cursor-pointer shadow-lg"
        title="Cookie Preferences"
      >
        <span className="sr-only">Open Cookie Preferences</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="30"
          height="30"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10h-2a2 2 0 0 1-2-2 2 2 0 0 1-2-2 2 2 0 0 1-2-2c0-1.1-.9-2-2-2V2z"/>
          <circle cx="8.5" cy="11.5" r="1.5" fill="#7c3aed"/>
          <circle cx="11" cy="16" r="1.5" fill="#7c3aed"/>
          <circle cx="14" cy="10" r="1.5" fill="#7c3aed"/>
          <circle cx="7" cy="15.5" r="1" fill="#7c3aed"/>
          <circle cx="15" cy="14" r="1" fill="#7c3aed"/>
        </svg>
      </button>
    </>
  );
}

export default CookieConsentModal;
